import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import { BrandCard } from "@/components/ui/brand-card";
import Seo from "@/components/Seo";
import { SERVICES_LIST } from "@/data/services";
import { MASONRY_SERVICE } from "@/data/masonry-service";
import { COMPANY } from "@/lib/constants";

// Residential services overview = 6 core trades + the new Masonry page.
const ALL_SERVICES = [...SERVICES_LIST, MASONRY_SERVICE];
import { PAGE_META } from "@/lib/seo-constants";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";

export default function Services() {
    return (
        <>
            <Seo
                {...PAGE_META.services}
                path="/services"
                jsonLd={[
                    localBusinessSchema(),
                    breadcrumbListSchema([
                        { name: "Services", path: "/services" },
                    ]),
                ]}
            />
            <Section dark className="py-20 md:py-24" data-testid="services-hero">
                <Container>
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                            What We Build
                        </p>
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                            data-testid="services-title"
                        >
                            Seven core trades.
                            <br />
                            <span className="text-brand-coral">One trusted crew.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/75 font-light italic leading-relaxed">
                            From the front fence to the kitchen reno — Reformity builds it,
                            project-manages it, and stands behind it.
                        </p>
                    </div>
                </Container>
            </Section>

            <Section data-testid="services-list">
                <Container>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {ALL_SERVICES.map((s, i) => (
                            <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                data-testid={`services-card-${s.slug}`}
                            >
                                <BrandCard className="h-full flex flex-col justify-between min-h-[200px]">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-brand-gray">
                                        0{i + 1}
                                    </span>
                                    <div className="mt-6">
                                        <h3 className="font-display text-3xl tracking-wide text-brand-dark group-hover:text-brand-cyan transition-colors">
                                            {s.name}
                                        </h3>
                                        <p className="mt-3 text-sm text-brand-dark/70 leading-relaxed">
                                            {s.heroSubhead}
                                        </p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-coral group-hover:text-brand-cyan transition-colors">
                                            View Service
                                            <ArrowRight
                                                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                                                strokeWidth={2.25}
                                            />
                                        </span>
                                    </div>
                                </BrandCard>
                            </Link>
                        ))}
                    </div>
                </Container>
            </Section>

            <Section dark className="py-16 md:py-20" data-testid="services-cta">
                <Container>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-white leading-tight">
                            Not sure which service you need?
                        </h2>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <BrandButton asChild variant="primary" size="lg">
                                <Link to="/contact">
                                    Get a Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                            <BrandButton asChild variant="outline" tone="onDark" size="lg">
                                <a href={COMPANY.phoneHref}>
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {COMPANY.phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
}
